// Import Sentry configuration for production environment
import '../sentry.client.config.ts';
import './index.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';

import { App } from './App';

// Only load DevTools in development
const ReactQueryDevtools = import.meta.env.DEV
  ? React.lazy(() =>
      import('@tanstack/react-query-devtools').then((module) => ({
        default: module.ReactQueryDevtools,
      })),
    )
  : () => null;

// Create a more optimized query client with better caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1, // Only retry once to avoid excessive network calls
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      gcTime: 1000 * 60 * 30, // Cache for 30 minutes (renamed from cacheTime)
    },
    mutations: {
      retry: 1, // etry once
      retryDelay: 1000, // Retry after 1 second
    },
  },
});

// Preload critical assets and chunks
if ('loading' in HTMLImageElement.prototype) {
  // Preload critical images
  const preloadImages = () => {
    const imageUrls: string[] = [
      // Add paths to critical images here
      // '/logo.png',
      // '/hero-image.jpg'
    ];

    imageUrls.forEach((url) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
    });
  };

  // Execute after initial render
  window.addEventListener('load', () => {
    // Delay non-critical preloads
    setTimeout(preloadImages, 1000);
  });
}

// Render the app
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />

      {import.meta.env.DEV && (
        <React.Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </React.Suspense>
      )}
    </QueryClientProvider>
  </React.StrictMode>,
);
